import * as React from 'react';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMiddlePanelData, resetMiddlePanel, setPanelVisibility } from "../../../containers/WorkkardDetail/actions/action";
import { getImagePreview, redirectURL, fetchDownload, formatSizeInKB } from '@/common/util';
import Icon from '../../../icons';
import { fileIconBasedOnFileName } from '../../../containers/WorkkardDetail/utils';
import { IMAGE_TYPE } from '../../../components/AttachedWorkkardFIles/workkardCell';

const MAX_VISIBLE_FILES = 4;
const FILES_TAB = 1;
const MAX_BASE_NAME_LENGTH = 35;
const TRUNCATED_HEAD_LENGTH = 20;
const TRUNCATED_TAIL_LENGTH = 8;

const ACTIVITIES_PANEL_VISIBILITY = {
    templateType: "A",
    isLeftVisible: true,
    isMiddleVisible: true,
    isRightVisible: true,
    isNoteVisible: false,
    isVisibleExternalApp: false,
    isNoteCreationVisible: false,
    isRightPanelExpand: true,
    toggleView: "Activities"
};

const getFileExtension = (fileName = '') => {
    const dotIndex = fileName?.lastIndexOf('.') ?? -1;
    return dotIndex === -1 ? '' : fileName.substring(dotIndex + 1).toLowerCase();
};

const truncateFileName = (fileName = '') => {
    if (!fileName) return '';
    return fileName.split('.')[0].length > MAX_BASE_NAME_LENGTH
        ? fileName.substring(0, TRUNCATED_HEAD_LENGTH) + "..." + fileName.slice(-TRUNCATED_TAIL_LENGTH)
        : fileName;
};

const getFileLabel = (file) => {
    const name = truncateFileName(file.file_name);
    return file.file_size ? `${name} (${formatSizeInKB(file.file_size)})` : name;
};

const openPdfPreview = async (e, file) => {
    e.preventDefault();
    // Open the tab synchronously so popup blockers treat it as user-initiated.
    const previewTab = window.open('', '_blank');
    if (!previewTab) return;
    previewTab.document.title = 'Loading preview...';
    try {
        const response = await fetchDownload(file.guid);
        previewTab.location.href = window.URL.createObjectURL(response.data);
    } catch (error) {
        previewTab.close();
        console.error(error);
    }
};

// getImagePreview fetches the preview; the generic image icon shows while loading or if it fails.
const WorkkardImagePreview = ({ guid, fileName }) => {
    const [previewUrl, setPreviewUrl] = useState(null);

    useEffect(() => {
        setPreviewUrl(null);
        if (!guid) return undefined;
        return getImagePreview(guid, setPreviewUrl);
    }, [guid]);

    return previewUrl
        ? <img src={previewUrl} alt={fileName || 'workkard file'} />
        : <Icon name="Image" className="icon mr" />;
};

const WorkkardFiles = ({ history }) => {
    const dispatch = useDispatch();
    const workkardFiles = useSelector(state => state.workkardDetail?.workkardDetail?.WorkkardFiles) || [];
    const middlePanel = useSelector(state => state.workkardDetail.middlePanel);
    const panelVisibility = useSelector(state => state.workkardDetail.panelVisibility);

    const workkardFileCount = workkardFiles.length;

    const moveToFileTab = () => {
        const isActivitiesView = panelVisibility?.toggleView == "Activities";
        const isFilesTab = middlePanel?.activeTab == FILES_TAB;
        const isShowingWorkkardFiles = !["All", "activity"].includes(middlePanel?.files?.type);
        // Already showing workkard files in the activities view.
        if (isActivitiesView && isFilesTab && isShowingWorkkardFiles) return;

        if (!isActivitiesView) {
            dispatch(setPanelVisibility(ACTIVITIES_PANEL_VISIBILITY));
        }
        redirectURL(history, { ...history?.location, hash: 'cf' });
        dispatch(resetMiddlePanel({ type: 'files', value: 'workkard' }));
        if (!isFilesTab) {
            dispatch(fetchMiddlePanelData({ activeTab: FILES_TAB }));
        }
    };

    const handleHeaderClick = () => {
        if (workkardFileCount > 0) moveToFileTab();
    };

    const renderFileListing = (file) => {
        const isImage = IMAGE_TYPE.has(file.file_type);
        const label = getFileLabel(file);

        return (
            <div className="rowSpaceBetween pdfDownload alignMiddle">
                <div className="filesPdf alignMiddle">
                    <button type="button" className="btn-cc__link btn-cc__link__blue" onClick={(e) => openPdfPreview(e, file)}>
                        {isImage
                            ? <WorkkardImagePreview guid={file.guid} fileName={file.file_name} />
                            : <Icon name={fileIconBasedOnFileName(getFileExtension(file.file_name))} className="icon mr" />
                        }
                        <span className='fileTitle' title={file.file_name}>{label}</span>
                    </button>
                </div>
                <span className="active alignMiddle">
                    <a href={file.file_url} target="_blank" rel="noopener noreferrer" aria-label={`Download ${file.file_name || 'file'}`}>
                        <Icon name="download" className="icon ml" />
                    </a>
                </span>
            </div>
        );
    };

    return (
        <div className="associatedWorkkard">
            <header className="rowSpaceBetween alignMiddle">
                <h3>
                    <span className='mr__4 flexGrow'>
                        Workkard Files ({workkardFileCount})
                    </span>
                </h3>
                {workkardFileCount > 0 && <button type="button" className="btn-cc__link btn-cc__link__blue mr" onClick={handleHeaderClick}>view all</button>}
            </header>

            {workkardFiles.slice(0, MAX_VISIBLE_FILES).map((file, index) => (
                <div key={file.guid || index} className="filesDownload">
                    {renderFileListing(file)}
                </div>
            ))}
        </div>
    );
};

export default WorkkardFiles;